import { Link } from "react-router-dom";
import { ArrowRight, Camera, DoorOpen, MessageSquareHeart, NotebookPen, Siren } from "lucide-react";
import { Reveal } from "../components/Reveal";
import { IMAGES } from "../data/content";
import { useLang } from "../i18n";

const ICONS = { NotebookPen, Camera, MessageSquareHeart, Siren };
const HL_COLORS = [
  { bg: "#FDE7E7", fg: "#D7352B" },
  { bg: "#FFF2C4", fg: "#B98A00" },
  { bg: "#E3EFFC", fg: "#2E7CD6" },
  { bg: "#E6F4EA", fg: "#3D9B4F" },
];

export default function ParentPortal() {
  const { t, c } = useLang();
  return (
    <>
      <section className="pt-44 pb-20 bg-forest text-cream grain relative overflow-hidden">
        <div className="absolute -bottom-32 -right-24 w-96 h-96 rounded-full bg-tree/30 blur-3xl" />
        <div className="absolute top-20 left-1/3 w-48 h-48 rounded-full bg-tangerine/15 blur-3xl" />
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 relative grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <Reveal>
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-amber">{t("pp_overline")}</p>
            <h1 className="mt-4 font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-tight">
              {t("pp_h1")}
            </h1>
            <p className="mt-6 text-cream/75 text-base sm:text-lg leading-relaxed max-w-xl">{t("pp_sub")}</p>
            <div className="mt-8 flex items-center gap-3 rounded-2xl bg-white/10 border border-white/15 px-5 py-4 max-w-md">
              <DoorOpen className="w-6 h-6 text-amber shrink-0" />
              <p className="text-sm text-cream/85">{t("pp_notice")}</p>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="hero-blob overflow-hidden aspect-[4/3] shadow-2xl shadow-black/30">
              <img src={IMAGES.preschool} alt="Teacher and children at The Learning Tree Academy" className="w-full h-full object-cover" />
            </div>
          </Reveal>
        </div>
      </section>

      <section data-testid="parent-portal-info-section" className="py-20 md:py-28 bg-cream">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
          <Reveal className="max-w-2xl">
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-tangerine">{t("pp_sec_overline")}</p>
            <h2 className="mt-4 font-display font-extrabold text-3xl sm:text-4xl text-forest tracking-tight">{t("pp_sec_h2")}</h2>
          </Reveal>
          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {c.portalHighlights.map((h, i) => {
              const Icon = ICONS[h.icon];
              const col = HL_COLORS[i % HL_COLORS.length];
              return (
                <Reveal key={h.title} delay={i * 0.08}>
                  <div className="rounded-3xl bg-white border border-bone p-7 h-full hover:-translate-y-1.5 hover:shadow-xl hover:shadow-forest/10 transition-all duration-400">
                    <div className="w-12 h-12 rounded-2xl flex items-center justify-center mb-5" style={{ background: col.bg }}>
                      <Icon className="w-6 h-6" style={{ color: col.fg }} />
                    </div>
                    <h3 className="font-display font-bold text-lg text-forest">{h.title}</h3>
                    <p className="mt-2 text-sm text-bark leading-relaxed">{h.desc}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
          <Reveal delay={0.2} className="mt-14 text-center">
            <Link to="/contact" className="group inline-flex items-center gap-2 rounded-full bg-tangerine text-white px-8 py-4 font-display font-bold hover:bg-clay transition-colors shadow-xl shadow-tangerine/30">
              {t("pp_cta")} <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
          </Reveal>
        </div>
      </section>
    </>
  );
}
